import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { FieldDef, SectionDef, FormValues } from '../types';
import FormField from './FormField';

interface FormSectionProps {
  section: SectionDef;
  index: number;
  values: FormValues;
  onChange: (key: string, value: string | string[]) => void;
  accentColor: string;
  fieldErrors?: Record<string, string>;
  expanded: boolean;
  onToggle: () => void;
}

function isFieldVisible(field: FieldDef, values: FormValues): boolean {
  if (!field.conditional) return true;

  const condValue = values[field.conditional.field];
  return Array.isArray(condValue)
    ? condValue.some((value) => field.conditional!.values.includes(value))
    : field.conditional.values.includes(condValue as string);
}

function getFieldGridSize(field: FieldDef) {
  if (field.type === 'textarea' || field.key === 'criterioDesconto') return 12;
  return { xs: 12, sm: 6 };
}

export default function FormSection({ section, index, values, onChange, accentColor, fieldErrors, expanded, onToggle }: FormSectionProps) {
  const visibleFields = section.fields.filter((field) => isFieldVisible(field, values));

  return (
    <Accordion expanded={expanded} onChange={onToggle} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' }, borderRadius: 1, overflow: 'hidden', mb: 1 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 48,
          '&.Mui-expanded': { minHeight: 48 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: accentColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1 }}>
              {index + 1}
            </Typography>
          </Box>
          <Typography variant="subtitle2" fontWeight={600}>
            {section.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            ({visibleFields.length} campo{visibleFields.length !== 1 ? 's' : ''})
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2.5, pb: 2, px: 2.5 }}>
        <Grid container spacing={2}>
          {visibleFields.map((field) => (
            <Grid key={field.key} size={getFieldGridSize(field)}>
              <FormField field={field} values={values} onChange={onChange} externalError={fieldErrors?.[field.key]} />
            </Grid>
          ))}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}
